import {getMatchGenerator, type Matchable} from './get-match'

export {textMatchable} from './get-match'

const SEARCH_RESULT_LIMIT = 200

interface Position {
  line: number
  column: number
}

export interface Range {
  start: Position
  end: Position
}

interface SearchReference {
  ident: {
    start: Position
  }
}

export function searchInBlobContent(lines: string[], matchable: Matchable): Range[] {
  const output: Range[] = []
  const matches = getMatchGenerator(matchable, lines)
  let match = matches.next()

  while (!match.done && output.length < SEARCH_RESULT_LIMIT) {
    const {column, columnEnd, line} = match.value
    output.push({
      start: {
        line,
        column,
      },
      end: {
        line,
        column: columnEnd,
      },
    })

    match = matches.next()
  }
  return output
}

export function searchInBlobIncrementally(codeRefs: SearchReference[], lines: string[], matchable: Matchable): Range[] {
  if (codeRefs.length === 0) {
    // In the incremental search we are narrowing results. If there are no results for a less specific search, there won't be any further matches.
    return []
  }

  const startedAtResultLimit = codeRefs.length >= SEARCH_RESULT_LIMIT

  const checkedLine = new Set<number>()
  const searchResults = codeRefs.reduce<Range[]>((output, ref) => {
    if (checkedLine.has(ref.ident.start.line)) {
      return output
    }

    checkedLine.add(ref.ident.start.line)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const matches = getMatchGenerator(matchable, [lines[ref.ident.start.line]!])
    let match = matches.next()
    while (!match.done && output.length < SEARCH_RESULT_LIMIT) {
      const {column, columnEnd} = match.value
      output.push({
        start: {
          line: ref.ident.start.line,
          column,
        },
        end: {
          line: ref.ident.start.line,
          column: columnEnd,
        },
      })
      match = matches.next()
    }

    return output
  }, [])

  // Add new results from the raw blob. Note: this is only necessary if we dropped the number
  // of results below the maximum in the previous phase!
  if (searchResults.length < SEARCH_RESULT_LIMIT && startedAtResultLimit) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startFromLine = codeRefs[codeRefs.length - 1]!.ident.start.line
    const matchesInRawBlob = getMatchGenerator(matchable, lines, startFromLine)
    let match = matchesInRawBlob.next()
    while (!match.done && searchResults.length < SEARCH_RESULT_LIMIT) {
      const {line, column, columnEnd} = match.value
      searchResults.push({
        start: {
          line,
          column,
        },
        end: {
          line,
          column: columnEnd,
        },
      })
      match = matchesInRawBlob.next()
    }
  }

  return searchResults
}
